/**
 * tokens.ts — Conversion Architect v2 design tokens.
 *
 * Follows the LogicDecomposer precedent: a token module + inline style objects,
 * no stylesheet classes. Every CA component styles itself from these values.
 */

import type { CSSProperties } from 'react';

export const c = {
  canvas: '#F5F7FB',
  card: '#FFFFFF',
  fill1: '#F8FAFC',
  fill2: '#F1F5F9',
  fill3: '#FAFBFC',

  textPrimary: '#0F172A',
  textBody: '#334155',
  textSecondary: '#64748B',
  textTertiary: '#94A3B8',
  tableLabel: '#475569',

  border: '#E2E8F0',
  borderControl: '#CBD5E1',
  hairline: '#F1F5F9',

  navy: '#0F1A3D',
  accent: '#7C3AED',
  accentGradient: 'linear-gradient(135deg, #4DD0E1, #7C3AED)',

  green: {
    fill: '#DCFCE7', fillSoft: '#F0FDF4',
    text: '#15803D', text2: '#16A34A',
    border: '#BBF7D0', border2: '#86EFAC',
    deep: '#14532D',
  },
  amber: {
    fill: '#FEF3C7', fillSoft: '#FFFBEB',
    text: '#92400E', text2: '#B45309',
    border: '#FDE68A', solid: '#F59E0B',
  },
  red: { fill: '#FEE2E2', text: '#B91C1C', border: '#FECACA' },
  blue: {
    fill: '#DBEAFE', fillSoft: '#EFF6FF',
    text: '#1E40AF', text2: '#2563EB',
    border: '#BFDBFE', border2: '#93C5FD',
  },
  purple: { fill: '#F3E8FF', text: '#6B21A8' },
  indigo: { fill: '#EEF2FF', border: '#C7D2FE', text: '#3730A3' },
} as const;

export const font = {
  ui: "'Plus Jakarta Sans', Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
} as const;

// ---------------------------------------------------------------------------
// Shared style fragments
// ---------------------------------------------------------------------------

export const panel: CSSProperties = {
  background: c.card,
  border: `1px solid ${c.border}`,
  borderRadius: 12,
  padding: 20,
};

export const eyebrow: CSSProperties = {
  fontFamily: font.ui,
  fontSize: 10,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  color: c.textSecondary,
};

export const inputStyle: CSSProperties = {
  fontFamily: font.ui,
  fontSize: 13,
  color: c.textPrimary,
  background: c.card,
  border: `1px solid ${c.borderControl}`,
  borderRadius: 8,
  padding: '8px 12px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box' as const,
};

export function btnPrimary(disabled?: boolean): CSSProperties {
  return {
    fontFamily: font.ui, fontSize: 13, fontWeight: 700, color: '#fff',
    background: disabled ? c.textTertiary : c.textPrimary,
    border: 'none', borderRadius: 999, padding: '9px 18px',
    cursor: disabled ? 'default' : 'pointer',
  };
}

export function btnSecondary(disabled?: boolean): CSSProperties {
  return {
    fontFamily: font.ui, fontSize: 13, fontWeight: 700, color: c.textBody,
    background: c.card, border: `1px solid ${c.borderControl}`,
    borderRadius: 999, padding: '8px 16px',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
  };
}

export function btnGreen(disabled?: boolean): CSSProperties {
  return {
    fontFamily: font.ui, fontSize: 13, fontWeight: 700, color: c.green.text,
    background: c.card, border: `1px solid ${c.green.border2}`,
    borderRadius: 999, padding: '8px 16px',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
  };
}

export function pill(bg: string, fg: string, border?: string): CSSProperties {
  return {
    display: 'inline-block',
    padding: '3px 11px',
    borderRadius: 999,
    fontFamily: font.ui,
    fontSize: 10.5,
    fontWeight: 800,
    whiteSpace: 'nowrap' as const,
    background: bg,
    color: fg,
    border: border ? `1px solid ${border}` : 'none',
  };
}

// ---------------------------------------------------------------------------
// Change-type pills (mapping_type) — plain language, tooltips
// ---------------------------------------------------------------------------

export const CHANGE_TYPES: Record<string, { label: string; bg: string; fg: string; tip: string }> = {
  DIRECT: {
    label: 'Unchanged', bg: c.green.fill, fg: c.green.text,
    tip: 'Same account, same meaning. Nothing to decide.',
  },
  SPLIT: {
    label: 'Splits in two', bg: c.blue.fill, fg: c.blue.text,
    tip: 'One old account becomes two or more new ones.',
  },
  CONSOLIDATE: {
    label: 'Combined', bg: c.purple.fill, fg: c.purple.text,
    tip: 'Two or more old accounts become one new one.',
  },
  ENRICH: {
    label: 'Needs new detail', bg: c.indigo.fill, fg: c.indigo.text,
    tip: 'The new chart asks for information the old one never carried.',
  },
  REBOOK: {
    label: 'Booked differently', bg: c.red.fill, fg: c.red.text,
    tip: 'The new system records this transaction with a different journal pattern.',
  },
  UNMAPPED: {
    label: 'No home yet', bg: c.amber.fill, fg: c.amber.text,
    tip: 'This account has a balance but no place in the new chart. Must be resolved before go-live.',
  },
};

// ---------------------------------------------------------------------------
// Status pills (mapping rule status)
// ---------------------------------------------------------------------------

export const STATUS_PILLS: Record<string, { label: string; bg: string; fg: string; border?: string }> = {
  proposed: { label: 'Waiting on you', bg: c.amber.fill, fg: c.amber.text, border: c.amber.border },
  confirmed: { label: '✓ Approved', bg: c.green.fill, fg: c.green.text },
  rejected: { label: 'Rejected', bg: c.red.fill, fg: c.red.text },
  pending_question: { label: 'Decision needed', bg: c.amber.fill, fg: c.amber.text, border: c.amber.border },
};

// ---------------------------------------------------------------------------
// Question types — plain language (never show Q_* enums)
// ---------------------------------------------------------------------------

export const QTYPE_LABELS: Record<string, string> = {
  Q_SPLIT_ALLOCATION: 'How should this account divide?',
  Q_CONSOLIDATION_CONFIRM: 'Confirm these accounts combine',
  Q_ATTRIBUTE_POPULATION: 'A new part of the account number needs a value',
  Q_REBOOK_DESIGN: 'This books differently in the new system',
  Q_UNMAPPED_DESIGN: 'No home in the new chart',
  Q_TARGET_UNMATCHED: 'New account with no old equivalent',
  Q_ENTITY_VARIATION: 'One entity does this differently',
  Q_COLUMN_MAPPING: 'Check how a column was read',
};

export const SEVERITY_CHIPS: Record<string, { bg: string; fg: string; border?: string }> = {
  critical: { bg: c.red.fill, fg: c.red.text, border: c.red.border },
  high: { bg: c.amber.fill, fg: c.amber.text, border: c.amber.border },
  medium: { bg: c.blue.fill, fg: c.blue.text, border: c.blue.border },
};

// ---------------------------------------------------------------------------
// Workstreams — value/label pairs matching the backend enum
// ---------------------------------------------------------------------------

export const WORKSTREAMS: Array<{ value: string; label: string }> = [
  { value: 'lease_accounting', label: 'Lease Accounting' },
  { value: 'ar', label: 'Accounts Receivable' },
  { value: 'ap', label: 'Accounts Payable' },
  { value: 'intercompany', label: 'Intercompany' },
  { value: 'fah', label: 'FAH — Product Based Postings' },
  { value: 'fixed_assets', label: 'Fixed Assets' },
];

export function workstreamLabel(value: string): string {
  const ws = WORKSTREAMS.find(w => w.value === value);
  return ws ? ws.label : value;
}

// ---------------------------------------------------------------------------
// Phase names + purpose copy (plain language, binding vocabulary)
// ---------------------------------------------------------------------------

export type PhaseKey = 'setup' | 'comparison' | 'mapping' | 'questions' | 'validation' | 'output';

export const PHASE_META: Record<PhaseKey, { label: string; forText: string; doText: string; oracleText: string }> = {
  setup: {
    label: 'Upload',
    forText: 'This is where a conversion project starts: the current chart of accounts from Oracle EBS and the new chart from Oracle Cloud, side by side.',
    doText: 'Create a project, upload the source and target files, and check the columns were read correctly.',
    oracleText: 'Oracle calls these the chart of accounts and its segments.',
  },
  comparison: {
    label: 'Structure changes',
    forText: "The two charts are built differently — the new one has parts of the account number the old one never had.",
    doText: "Review what's new, what's gone, and how the two structures line up before any account-level mapping.",
    oracleText: 'Oracle calls this the chart of accounts structure — the segments and value sets.',
  },
  mapping: {
    label: 'Account mapping',
    forText: 'Every old account gets a proposed destination in the new chart, each labeled with what changes.',
    doText: 'Approve the unchanged ones, and look closer at anything that splits, combines, or books differently.',
    oracleText: 'Oracle calls this the crosswalk.',
  },
  questions: {
    label: 'Decisions',
    forText: "Most accounts move across on their own. A few can't, because the new chart asks a question the old one never did.",
    doText: 'Answer each decision — your answer is recorded with your name and applies everywhere.',
    oracleText: 'These become the mapping rules and BRD content.',
  },
  validation: {
    label: 'Checks',
    forText: 'Before anything converts, the mapping has to clear a set of gates: nothing unmapped, balances tie out, every decision closed.',
    doText: "Run the checks and resolve anything that isn't green.",
    oracleText: 'Oracle configuration teams call this conversion validation and reconciliation.',
  },
  output: {
    label: 'Documents',
    forText: 'The mapping is not the deliverable — the documentation it produces is.',
    doText: 'Generate the mapping workbook, the BRD narrative, and the FRD for the configuration team.',
    oracleText: 'The BRD is signed off by Controllers; the FRD goes to the downstream development teams.',
  },
};
